<script>
	import configuration from '../configuration.json';
    import app from '../data/app.svelte.js';
    import persistent from '../data/persistent.svelte';
    import { currentSession, currentSessionId } from '../data/session.svelte.js';

    import { onMount, onDestroy } from 'svelte';
    import { _, locale } from 'svelte-i18n';
    import { get } from 'svelte/store';
    import { next, previous, goto, replace } from '../helpers/navigation.svelte.js';
    import { escapeHtml } from '../helpers/escape.js';
    import Icon from '../components/Icon.svelte';
    import arrowRightIcon from '../assets/icons/arrow-right.svg?raw';
    import closeIcon from '../assets/icons/close.svg?raw';

    import Section from '../components/Section.svelte';
    import Status from '../components/Status.svelte';
    import Navigation from '../components/Navigation.svelte';
    import Button from '../components/Button.svelte';
    import Appointment from '../components/Confirm/Appointment.svelte';
    import Code from '../components/Verify/Code.svelte';
    import Loader from '../components/Loader.svelte';

    const current = 'verify';

    let code = $state('');
    let isValidCode = $derived(code.length === 4);

    const urlParams = new URLSearchParams(window.location.search);
    let autoVerifying = $state(urlParams.has('appointment') && urlParams.has('code'));


    /* Polling for appointment status */

    let pollingInterval;

    async function checkAppointmentStatus() {
        try {
            const form = new FormData();
            form.append('session', currentSessionId);
            form.append('client', currentSession.client);
            form.append('salon', currentSession.salon);
            form.append('appointment', currentSession.id);

            const response = await fetch(`${configuration.endpoint}/OnlineAppointment.Remote.Appointments/get`, {
                method: 'POST',
                body: form,
                headers: {
                    'Authorization': `Bearer ${configuration.apikey}`,
                    'Accept-Language': get(locale)
                }
            });

            if (response.ok) {
                const data = await response.json();

                if (data.status === 'confirmed') {
                    // Store date and time from response
                    if (data.date) currentSession.date = data.date;
                    if (data.time) currentSession.time = data.time;

                    clearInterval(pollingInterval);
                    goto('success', 'right');
                }
            }
        } catch (error) {
        }
    }


    /* Reset the code */

    function resetCode() {
        code = '';
    }


    /* Verify the code */

    async function verifyCode() {
        if (!isValidCode) {
            return;
        }

        const form = new FormData();
        form.append('session', currentSessionId);
        form.append('client', currentSession.client);
        form.append('salon', currentSession.salon);
        form.append('appointment', currentSession.id);
        form.append('code', code);

        try {
            const response = await fetch(`${configuration.endpoint}/OnlineAppointment.Remote.Appointments/verify`, {
                method: 'POST',
                body: form,
                headers: {
                    'Authorization': `Bearer ${configuration.apikey}`,
                    'Accept-Language': get(locale)
                }
            });

            if (!response.ok) {
                if (response.status === 406) {
                    resetCode();
                }
                else if (response.status === 409) {
                    replace('error/verify-time-no-longer-available');
                }
                else {
                    replace('error/verify-failed');
                }

                return;
            }

            const data = await response.json();

            if (data.status === 'confirmed') {
                if (data.date) currentSession.date = data.date;
                if (data.time) currentSession.time = data.time;

                goto('success', 'right');
            } else {
                resetCode();
            }
        } catch (error) {
            replace('error/verify-failed');
        }
    }


    /* Cancel the appointment */

    async function cancelAppointment() {
        const form = new FormData();
        form.append('session', currentSessionId);
        form.append('client', currentSession.client);
        form.append('salon', currentSession.salon);
        form.append('appointment', currentSession.id);
        form.append('token', currentSession.token);

        try {
            const response = await fetch(`${configuration.endpoint}/OnlineAppointment.Remote.Appointments/cancel`, {
                method: 'POST',
                body: form,
                headers: {
                    'Authorization': `Bearer ${configuration.apikey}`,
                    'Accept-Language': get(locale)
                }
            });

            if (response.ok) {
                // Appointment cancelled successfully
                clearInterval(pollingInterval);
                goto('cancelled', 'right');
            } else {
                replace('error/cancel-failed');
            }
        } catch (error) {
            replace('error/cancel-failed');
        }
    }


    /* Component lifecycle */

    onMount(async () => {

        /* The code is in the url, verify immediately */
        
        if (autoVerifying) {

            /* Show the loader for at least 1 second to show we are working on it */
            await new Promise(r => setTimeout(r, 1000)); 

            /* Verify the code from the URL */
            currentSession.id = urlParams.get('appointment');
            code = urlParams.get('code');
            await verifyCode();

            /* If the verification fails, ask the user for the code */
            autoVerifying = false;
        } 
                
        /* Or, ask the user for the code, and poll for status updates */
        
        else {
            pollingInterval = setInterval(checkAppointmentStatus, 4000);
            checkAppointmentStatus();
        }
    });

    onDestroy(() => {
        if (pollingInterval) {
            clearInterval(pollingInterval);
        }
    });


</script>

<svelte:head>
    <title>{$_('appointment_title')} - {$_('confirm')}</title>
</svelte:head>

<Section title={$_('confirm')}>
    {#if autoVerifying}
        <Loader delay={0} />
    {:else}
        <p>
            {@html $_('verification_email_sent', { values: { email: escapeHtml(currentSession.customer?.email || '') } })}
        </p>
        <p>
            {$_('verification_code_instructions')}
        </p>

        <Code bind:value={code} onSubmit={verifyCode} />
    {/if}

    {#snippet navigation()}
        {#if !autoVerifying}
            <Navigation>
                <Button onclick={cancelAppointment} disabled={isValidCode}>
                    <Icon data={closeIcon} />
                    {$_('cancel')}
                </Button>

                <Button color="accent" onclick={verifyCode} disabled={!isValidCode}>
                    {$_('confirm')}
                    <Icon data={arrowRightIcon} />
                </Button>
            </Navigation>
        {/if}
    {/snippet}
</Section>